/**
 * loginViewModel — l'état de l'écran `/login` → l'étape que montre `<LoginPage>`.
 *
 * Pur : le réducteur de l'écran (ce que la personne a tapé, où elle en est, ce que le serveur a
 * répondu) et les gestes du crochet entrent, le `content` de la page sort. Portage littéral de
 * l'ancien `auth/LoginScreen.js` : chaque règle cite le symbole qu'elle reprend.
 *
 * Les erreurs ne restent pas dans les tranches de l'api comme dans l'ancien écran : le crochet les
 * range ici (`errors`) et les efface aux mêmes gestes (une frappe, un changement d'étape), ce qui
 * revient au même à l'écran.
 */
import { LOGIN_STEPS, LOGIN_MODES } from "../../../../../data/auth/loginSteps"
import { type UILoginCodeChannel, type UILoginMode } from "../../../ui/types"

/** Où le code est parti — `data/auth/loginCodeSent.codeSentTo`. */
export type LoginCodeSentTo = {
    identifier: string
    channel?: UILoginCodeChannel
    emailHasLink: boolean
}

/** Les trois refus du serveur que l'écran montre, tels que l'api les a rangés. */
export type LoginErrors = {
    request: unknown
    verify: unknown
    password: unknown
}

export type LoginUiState = {
    step: (typeof LOGIN_STEPS)[keyof typeof LOGIN_STEPS]
    mode: UILoginMode
    email: string
    phone: string
    password: string
    code: string
    /** Une adresse connue s'affiche, elle ne se retape pas (`emailLocked` de l'ancien écran). */
    emailLocked: boolean
    emailError: string | null
    sentTo: LoginCodeSentTo | null
    resendIn: number
    /** « Nouveau code envoyé. » — dès le deuxième envoi. */
    resent: boolean
    /** Arrivé par « Mot de passe oublié ? » : « Choisir un nouveau mot de passe » est offert. */
    forgotPassword: boolean
    errors: LoginErrors
}

export const NO_LOGIN_ERRORS: LoginErrors = { request: null, verify: null, password: null }

/** Le champ du code ne sert plus : expiré, ou plus d'essai (`codeIsDead`). */
export function isCodeDead(verifyError: unknown): boolean {
    return !!(verifyError as { expired?: unknown } | null)?.expired
}

export type LoginHandlers = {
    onSwitchMode: () => void
    onEmailChange: (email: string) => void
    onPhoneChange: (phone: string) => void
    onContinue: () => void
    onOpenTerms: () => void
    onBecomeTutor: () => void
    onEditEmail: () => void
    onPasswordChange: (password: string) => void
    onSubmitPassword: () => void
    onForgotPassword: () => void
    onRequestEmailCode: () => void
    onCodeChange: (code: string) => void
    onSubmitCode: () => void
    onResend: () => void
    onChangeIdentifier: () => void
    onUsePassword: () => void
    onChooseNewPassword: () => void
}

export type LoginStatus = {
    requesting: boolean
    verifying: boolean
    loggingIn: boolean
    /** La connexion s'est faite dans un autre onglet (web) : « Vous êtes connecté ». */
    connectedElsewhere: boolean
    /** L'arrivée du fast-track : « Votre mot de passe vous a été envoyé par email », rien à modifier. */
    passwordIndication: boolean
    /** Le web prend le focus du premier champ ; un téléphone garde son clavier fermé. */
    isWeb: boolean
    /** Les refus du serveur, déjà dits en mots par le crochet (`authErrorText`). */
    errorTexts: { request?: string; verify?: string; password?: string }
}

/** L'étape montrée, avec ses données et ses gestes — `renderStep` de l'ancien écran. */
export function loginPageContent(ui: LoginUiState, status: LoginStatus, on: LoginHandlers) {
    if (status.connectedElsewhere) return { step: "connectedElsewhere" as const }

    if (ui.sentTo) {
        const verifyError = ui.errors.verify
        return {
            step: "code" as const,
            channel: ui.sentTo.channel ?? "email",
            identifier: ui.sentTo.identifier,
            code: ui.code,
            onCodeChange: on.onCodeChange,
            error: status.errorTexts.verify,
            dead: isCodeDead(verifyError),
            emailHasLink: ui.sentTo.emailHasLink,
            verifying: status.verifying,
            onSubmit: on.onSubmitCode,
            resendIn: ui.resendIn,
            resending: status.requesting,
            onResend: on.onResend,
            resent: ui.resent && verifyError == null,
            resendError: status.errorTexts.request,
            onChangeIdentifier: on.onChangeIdentifier,
            onUsePassword: ui.sentTo.channel === "email" ? on.onUsePassword : undefined,
            onChooseNewPassword: ui.forgotPassword ? on.onChooseNewPassword : undefined,
        }
    }

    if (ui.step === LOGIN_STEPS.PASSWORD) {
        return {
            step: "password" as const,
            email: ui.email,
            onEmailChange: ui.emailLocked ? undefined : on.onEmailChange,
            emailError: ui.emailError ?? undefined,
            onEditEmail: ui.emailLocked && !status.passwordIndication ? on.onEditEmail : undefined,
            passwordSentByEmail: status.passwordIndication,
            password: ui.password,
            onPasswordChange: on.onPasswordChange,
            passwordError: status.errorTexts.password,
            loggingIn: status.loggingIn,
            autoFocusPassword: status.isWeb && ui.emailLocked,
            onSubmit: on.onSubmitPassword,
            onForgotPassword: on.onForgotPassword,
            onRequestCode: on.onRequestEmailCode,
            requestingCode: status.requesting,
            codeRequestError: status.errorTexts.request,
            onOpenTerms: on.onOpenTerms,
        }
    }

    const phoneMode = ui.mode === LOGIN_MODES.PHONE
    return {
        step: "start" as const,
        mode: ui.mode,
        onSwitchMode: on.onSwitchMode,
        email: ui.email,
        onEmailChange: on.onEmailChange,
        emailError: ui.emailError ?? undefined,
        phone: ui.phone,
        onPhoneChange: on.onPhoneChange,
        // Sous le numéro seulement : sur l'e-mail, « Continuer » n'envoie rien.
        phoneError: phoneMode ? status.errorTexts.request : undefined,
        sending: phoneMode && status.requesting,
        autoFocusPhone: status.isWeb,
        onContinue: on.onContinue,
        onOpenTerms: on.onOpenTerms,
        onBecomeTutor: on.onBecomeTutor,
    }
}
